import {
  btnUtils,
  isMobileCheck,
  isObject,
  makeToast,
  sellingListingValidateImages,
  serializeArray,
  serializeForm,
} from "./utils";
import { recModalOpen, recModalsClose } from "./modals";
import { formValidation } from "./formValidation";
import { imageDropzonesInit } from "./imageDropzones";
import Sortable from "sortablejs";
import { CountryProvinceSelector } from "./countryProvinceSelector";
import tippy from "tippy.js";
import type { RecDropzoneFiles, RecDropzoneFilesCompressed, CallerObject } from "./types";

/* Enable Tooltips */
tippy("[data-tippy-content]", { allowHTML: true });

/* Seller Listing Form Features */
export const sellerListingFormInit = (
  dropzoneObjects: Dropzone[],
  recDropzoneFiles: RecDropzoneFiles,
  recDropzoneFilesCompressed: RecDropzoneFilesCompressed,
  recDropzonePreloaded: Record<string, string>,
) => {
  /* Shipping Address Country/Province Selector */
  const setupAddressCountries = () => {
    new CountryProvinceSelector(
      `rec-shipping-address-country`,
      `rec-shipping-address-state`,
      {
        hideElement: `rec-shipping-address-state-container`,
      },
    );

    /* If we have specific countries included, limit address countries to this list */
    if (
      "countries" in recAppProxyConfig.settings.geo &&
      recAppProxyConfig.settings.geo.countries.length > 0
    ) {
      document
        .querySelectorAll("[data-address-country-select] option")
        .forEach((countryOption) => {
          const option = countryOption as HTMLOptionElement;
          if (
            option.value !== "---" &&
            recAppProxyConfig.settings.geo.countries.indexOf(option.value) < 0
          ) {
            option.remove();
          }
        });
    }
  };

  /* Shipping Address Modal Set */

  /* Shipping Address Form Submission */
  document
    .querySelectorAll(".rec-shipping-address-submit")
    .forEach((shippingAddressBtn) => {
      shippingAddressBtn.addEventListener("click", async (e) => {
        e.preventDefault();
        const target = e.target as HTMLButtonElement;
        const shippingAddressForm = target.closest(
          ".rec-shipping-address-form",
        ) as HTMLDivElement | null;
        const sellerForm = target.closest("form") as HTMLFormElement | null;

        if (!shippingAddressForm || !sellerForm) {
          console.error("Form elements not found");
          return;
        }

        btnUtils.loadingToggle(target);

        if (formValidation(shippingAddressForm, "")) {
          const shippingAddressFormData = serializeArray(shippingAddressForm);

          /* save address data */
          const addressData = await fetch(
            recAppProxyConfig.paths.pathAddressSave.replace(
              "%aid%",
              shippingAddressFormData.seller_address_id_edit,
            ),
            {
              method: "POST",
              body: JSON.stringify({ address: shippingAddressFormData }),
              headers: { "Content-Type": "application/json" },
            },
          );

          const addressDataResponse = await addressData.json();
          if (
            !addressDataResponse.errors &&
            isObject(addressDataResponse.data) &&
            "address" in addressDataResponse.data
          ) {
            /* for a new address, clone the UI for the address display and input */
            let shippingAddressDisplayElem: HTMLElement | null;
            if (shippingAddressFormData.seller_address_id_edit == "0") {
              const shippingAddressList = sellerForm.querySelector(
                ".rec-slf-shipping-address-list",
              ) as HTMLElement | null;
              if (!shippingAddressList) {
                console.error("Shipping address list element not found");
                return;
              }
              let shippingAddressDisplayNew = sellerForm
                .querySelector(
                  `.rec-slf-shipping-address-display[data-addr-id="default"]`,
                )
                ?.cloneNode(true) as HTMLElement | null;
              if (!shippingAddressDisplayNew) {
                console.error("Shipping address display new element not found");
                return;
              }
              shippingAddressDisplayNew.classList.remove("rec-hidden");
              shippingAddressDisplayNew.setAttribute(
                "data-addr-id",
                addressDataResponse.data.address.id,
              );
              const inputElement = shippingAddressDisplayNew.querySelector(
                "input",
              ) as HTMLInputElement | null;
              if (inputElement) {
                inputElement.disabled = false;
                inputElement.value = addressDataResponse.data.address.id;
                inputElement.setAttribute(
                  "data-addr",
                  JSON.stringify(addressDataResponse.data.address),
                );
                inputElement.checked = true;
              }
              const editElement = shippingAddressDisplayNew.querySelector(
                ".rec-slf-shipping-address-edit",
              ) as HTMLAnchorElement | null;
              if (editElement) {
                editElement.setAttribute(
                  "data-addr-id",
                  addressDataResponse.data.address.id,
                );
                editElement.addEventListener("click", async (e) => {
                  e.preventDefault();
                  await addressFieldsSelect(e.target as HTMLAnchorElement);
                });
              }
              shippingAddressList.appendChild(shippingAddressDisplayNew);
              shippingAddressDisplayElem = shippingAddressDisplayNew;
            } else {
              shippingAddressDisplayElem = sellerForm.querySelector(
                `.rec-slf-shipping-address-display[data-addr-id="${shippingAddressFormData.seller_address_id_edit}"]`,
              ) as HTMLElement | null;
            }

            if (shippingAddressDisplayElem) {
              let shippingAddressDisplay =
                addressDataResponse.data.address.firstName +
                " " +
                addressDataResponse.data.address.lastName;
              shippingAddressDisplay +=
                "<br>" +
                addressDataResponse.data.address.address1 +
                " " +
                addressDataResponse.data.address.address2 +
                "<br>" +
                addressDataResponse.data.address.city +
                ", " +
                addressDataResponse.data.address.province +
                " " +
                addressDataResponse.data.address.zip +
                " " +
                addressDataResponse.data.address.country;
              const contentElement = shippingAddressDisplayElem.querySelector(
                ".rec-slf-shipping-address-content",
              ) as HTMLElement | null;
              if (contentElement) {
                contentElement.innerHTML = shippingAddressDisplay;
              }
              shippingAddressDisplayElem.classList.remove("rec-hidden");
              recModalsClose();
            } else {
              console.error("Shipping address display element not found");
            }
          } else {
            makeToast("toast-address-error");
          }
        }
        btnUtils.reset();
      });
    });

  /* First/Default Address Display */
  document
    .querySelectorAll(".rec-slf-first-address")
    .forEach((firstAddress) => {
      const firstAddressElement = firstAddress as HTMLDivElement;
      document
        .querySelectorAll(".rec-slf-shipping-address-modal")
        .forEach((shipModal) => {
          const shipModalElement = shipModal as HTMLElement;
          const parentForm = shipModalElement.closest(
            ".rec-slf-form",
          ) as HTMLFormElement | null;
          if (parentForm) {
            const thisShippingForm = parentForm.querySelector(
              ".rec-modal-body",
            ) as HTMLDivElement | null;
            if (thisShippingForm) {
              firstAddressElement.appendChild(thisShippingForm);
            } else {
              console.error("Shipping form element not found");
            }
          } else {
            console.error("Parent form element not found");
          }
        });
    });

  /* Shipping Address Add/Edit */
  const addressFieldsApply = async (parentForm: HTMLFormElement) => {
    const addressInput = parentForm.querySelector(
      ".rec-slf-shipping-address-select:checked",
    );
    const firstAddress = parentForm.querySelector(".rec-slf-first-address");
    if (addressInput && !firstAddress) {
      addressFieldsSet(
        parentForm,
        JSON.parse(addressInput.getAttribute("data-addr") || ""),
      );
    }
  };
  // TODO: The any type below needs to be reverse-engineered. It could be null or an object representing some shape of address.
  const addressFieldsSet = (parentForm: HTMLFormElement, addressData: any) => {
    // null check so we aren't erroring when trying to reassign values
    if (addressData) {
      // reassign the firstName & lastName due to props being set differently in different places
      addressData = {
        ...addressData,
        firstName: addressData.firstName !== undefined ? addressData.firstName : addressData.first_name,
        lastName: addressData.lastName !== undefined ? addressData.lastName : addressData.last_name
      }
    }
    const addressFieldClasses = [
      { input: "rec-shipping-address-id", field: "id" },
      { input: "rec-shipping-address-first_name", field: "firstName" },
      { input: "rec-shipping-address-last_name", field: "lastName" },
      { input: "rec-shipping-address-phone", field: "phone" },
      { input: "rec-shipping-address-address1", field: "address1" },
      { input: "rec-shipping-address-address2", field: "address2" },
      { input: "rec-shipping-address-country", field: "country" },
      { input: "rec-shipping-address-city", field: "city" },
      { input: "rec-shipping-address-state", field: "province" },
      { input: "rec-shipping-address-zip", field: "zip" },
    ];
    for (let addressInput of addressFieldClasses) {
      let thisField = parentForm.querySelector(`.${addressInput.input}`) as HTMLInputElement | HTMLSelectElement | null;
      if (thisField !== null) {
        let thisFieldDefault =
          addressInput.field == "country" ? (thisField as HTMLSelectElement).options[0].value : "";
        thisFieldDefault = addressInput.field == "id" ? "0" : thisFieldDefault;
        thisField.value =
          addressData &&
            addressInput.field in addressData &&
            addressData[addressInput.field] &&
            addressData[addressInput.field].toString().trim().length > 0
            ? addressData[addressInput.field]
            : thisFieldDefault;
        thisField.dispatchEvent(new Event("change"));
      }
    }
    //parentForm.querySelector(".rec-shipping-address-default").checked = false;
  };
  const addressFieldsSelect = async (selectCta: HTMLAnchorElement) => {
    const listingForm = selectCta.closest("form") as HTMLFormElement | null;
    let addressId = selectCta.getAttribute("data-addr-id") || "";
    if (listingForm !== null) {
      const addressModal = listingForm.querySelector(
        ".rec-slf-shipping-address-modal",
      );
      /* New Address */
      if (addressId == "0" && addressModal !== null) {
        addressModal.classList.remove("rec-slf-shipping-address-editing");
        addressFieldsSet(listingForm, null);
        recModalOpen("rec-slf-shipping-address-modal", selectCta);
      } else if (addressModal !== null) {
        addressModal.classList.add("rec-slf-shipping-address-editing");
      }
      // convert to legacy id for path
      if (addressId.includes("gid")) {
        addressId = addressId.split("?")[0].replace("gid://shopify/MailingAddress/", "");
      }
      /* Retrieve address data */
      const addressData = await fetch(
        recAppProxyConfig.paths.pathAddressGet.replace("%aid%", addressId),
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
      );
      const addressDataResponse = await addressData.json();
      // convert id to legacy id
      if (addressDataResponse.id.length > 0) {
        const id = addressDataResponse.id.split("?")[0].replace("gid://shopify/MailingAddress/", "");
        addressDataResponse.id = id;
      }
      if (
        addressDataResponse &&
        isObject(addressDataResponse)
      ) {
        addressFieldsSet(listingForm, addressDataResponse);
        recModalOpen("rec-slf-shipping-address-modal", selectCta);
      } else {
        makeToast("toast-ajax-error");
      }
    }
  };
  document
    .querySelectorAll(".rec-slf-shipping-address-edit")
    .forEach((shippingAddressEdit) => {
      shippingAddressEdit.addEventListener("click", async (e) => {
        e.preventDefault();
        await addressFieldsSelect(e.target as HTMLAnchorElement);
      });
    });

  setupAddressCountries();

  /* Listen for the change of the listing condition field in order to obtain a price recommendation */
  if (recAppProxyConfig.settings.slf.pricing.price_recommendation_enabled) {
    document
      .querySelectorAll(".rec-slf-listing-condition")
      .forEach((conditionSelect) => {
        conditionSelect.addEventListener("change", async (e) => {
          e.preventDefault();
          const target = e.target as HTMLSelectElement;
          if (target.value !== "") {
            const sellerForm = target.closest("form") as HTMLFormElement | null;
            const retailPriceElem = sellerForm?.querySelector(
              ".rec-variant-price",
            ) as HTMLInputElement | null;
            const usedPriceElem = sellerForm?.querySelector(
              ".rec-slf-listing-price",
            ) as HTMLInputElement | null;
            const shippingPriceElem = sellerForm?.querySelector(
              ".rec-shipping-price",
            ) as HTMLInputElement | null;

            if (retailPriceElem && shippingPriceElem && usedPriceElem) {
              // ok, let's go fetch a recommended price for this item
              const currentSearchParams = new URLSearchParams(
                document.location.search,
              );
              const today =
                currentSearchParams.has("pd") &&
                  parseInt(currentSearchParams.get("pd")!)
                  ? new Date(parseInt(currentSearchParams.get("pd")!) * 1000)
                  : new Date();
              const priceRecommendation = await fetch(
                recAppProxyConfig.paths.pathPriceRecommendation,
                {
                  method: "POST",
                  body: JSON.stringify({
                    purchase_date: today.toISOString().split("T")[0],
                    product_price: parseFloat(retailPriceElem.value),
                    listing_condition: parseInt(target.value),
                    shipping_price: parseFloat(shippingPriceElem.value),
                  }),
                  headers: { "Content-Type": "application/json" },
                },
              );
              const priceRecommendationResponse =
                await priceRecommendation.json();
              if (!priceRecommendationResponse.errors) {
                usedPriceElem.setAttribute(
                  "placeholder",
                  usedPriceElem.getAttribute("data-recommended-text") +
                  recAppProxyConfig.settings.shop.currency_code +
                  parseFloat(
                    priceRecommendationResponse.pricing.recommended_price,
                  ).toFixed(0),
                );
                usedPriceElem.setAttribute(
                  "data-price-default",
                  parseFloat(
                    priceRecommendationResponse.pricing.recommended_price,
                  ).toFixed(2),
                );
              }
            }
          }
        });
      });
  }

  /* Listen for change/keyup of pricing field and modify/populate description prompts accordingly */
  const listingPriceChange = (e: Event) => {
    const target = e.currentTarget as HTMLInputElement;
    const listingPrice = parseFloat(target.value);
    const shippingPriceElem = document.querySelector(
      "#rec-shipping-price",
    ) as HTMLInputElement | null;
    const pricingDescriptionDefault = document.querySelector(
      ".rec-slf-description-listing-price-default",
    ) as HTMLSpanElement | null;
    const pricingDescriptionCalculated = document.querySelector(
      ".rec-slf-description-listing-price-calculated",
    ) as HTMLSpanElement | null;
    const salePriceSpan = document.querySelector(
      ".pricing-sale-price",
    ) as HTMLSpanElement | null;
    const payoutCreditSpan = document.querySelector(
      ".pricing-payout-credit",
    ) as HTMLSpanElement | null;
    const payoutCashSpan = document.querySelector(
      ".pricing-payout-cash",
    ) as HTMLSpanElement | null;

    if (!shippingPriceElem) {
      console.error("Shipping price element not found");
      return;
    }

    const shippingPrice = parseFloat(shippingPriceElem.value);

    if (!isNaN(listingPrice) && listingPrice > 0) {
      if (pricingDescriptionDefault) {
        pricingDescriptionDefault.classList.add("rec-hidden");
      }
      if (pricingDescriptionCalculated) {
        // Apply the calculations needed
        const salePrice = listingPrice + shippingPrice;
        if (salePriceSpan) {
          salePriceSpan.innerHTML = recAppProxyConfig.settings.shop.currency_code + salePrice.toString();
        }
        const payoutCash = parseFloat(
          (
            listingPrice *
            recAppProxyConfig.settings.payout.cash.payout_percentage
          ).toFixed(2),
        );
        if (payoutCashSpan) {
          payoutCashSpan.innerHTML = recAppProxyConfig.settings.shop.currency_code + payoutCash.toString();
        }
        const payoutCredit = parseFloat(
          (
            listingPrice *
            recAppProxyConfig.settings.payout.credit.payout_percentage
          ).toFixed(2),
        );
        if (payoutCreditSpan) {
          payoutCreditSpan.innerHTML = recAppProxyConfig.settings.shop.currency_code + payoutCredit.toString();
        }

        pricingDescriptionCalculated.classList.remove("rec-hidden");
      }
    } else {
      if (pricingDescriptionDefault) {
        pricingDescriptionDefault.classList.remove("rec-hidden");
      }
      if (pricingDescriptionCalculated) {
        pricingDescriptionCalculated.classList.add("rec-hidden");
      }
    }
  };

  const listingPriceInput = document.querySelector(
    "#rec-slf-listing-price",
  ) as HTMLInputElement | null;

  if (listingPriceInput) {
    listingPriceInput.addEventListener("change", listingPriceChange);
    listingPriceInput.addEventListener("keyup", listingPriceChange);
  } else {
    console.error("Listing price input element not found");
  }

  /* Main function to submit the listing form as either a new/updated listing or new/updated draft */
  const sellerListingFormExec = async (sellerListingForm: HTMLFormElement, options: CallerObject) => {
    /* Simple function to check the size of our payload array before submission */
    const getSizeInMB = (obj: any): number => {
      let str: string | null = null;
      if (typeof obj === "string") {
        // If obj is a string, then use it
        str = obj;
      } else {
        // Else, make obj into a string
        str = JSON.stringify(obj);
      }
      // Get the length of the Uint8Array
      const bytes = new TextEncoder().encode(str).length / 1000 / 1000;
      return bytes;
    };
    const thisCaller = options?.caller || "";

    //retrieve form data
    let formData = serializeForm(sellerListingForm);

    //handle AVS selection and remove data from form data
    if (formData.rec_slf_avs_choice == "avs") {
      formData.seller_address_line1 = formData.seller_address_line1_avs;
      formData.seller_address_line2 = formData.seller_address_line2_avs;
      formData.seller_city = formData.seller_city_avs;
      formData.seller_state = formData.seller_state_avs;
      formData.seller_postal = formData.seller_postal_avs;
      formData.seller_address_resave = 1; // force flag to resave this address data to Shopify
    }
    delete formData.rec_slf_avs_choice;
    delete formData.seller_first_name_avs;
    delete formData.seller_last_name_avs;
    delete formData.seller_address_line1_avs;
    delete formData.seller_address_line2_avs;
    delete formData.seller_city_avs;
    delete formData.seller_state_avs;
    delete formData.seller_postal_avs;
    delete formData.seller_country_avs;

    let postPath = "";
    let toastVar = "";
    let isDraft = false;
    let isPriceDrop = false;
    switch (formData.action_type) {
      case "listing":
        postPath = recAppProxyConfig.paths.pathListingPost;
        toastVar = "tlc";
        break;
      case "draft":
        postPath = recAppProxyConfig.paths.pathDraftPost;
        toastVar = "tds";
        isDraft = true;
        break;
      case "patch":
        if (thisCaller == "priceDrop") {
          postPath = recAppProxyConfig.paths.pathListingPatch.replace(
            "%lid%",
            `${formData["listing_id"]}/price_drop`,
          );
          isPriceDrop = true;
        } else {
          postPath = recAppProxyConfig.paths.pathListingPatch.replace(
            "%lid%",
            formData["listing_id"],
          );
        }
        toastVar = "tls";
        break;
      case "patch_draft":
        postPath = recAppProxyConfig.paths.pathDraftPatch.replace(
          "%lid%",
          formData["listing_id"],
        );
        toastVar = "tds";
        isDraft = true;
        break;
    }

    if (isPriceDrop || sellingListingValidateImages(sellerListingForm, -1)) {
      //check/append images
      sellerListingForm
        .querySelectorAll(".rec-slf-entry .dz-preview")
        .forEach((thisPreviewElem, index) => {
          const thisImageKey = thisPreviewElem.getAttribute("data-image-key");
          const thisImageInputName = `listing_image${index + 1}`;
          if (thisImageKey !== null) {
            const thisImageInputKey = thisImageKey.replace("-", "_");
            if (thisImageKey.indexOf("listing-image") === 0) {
              formData[thisImageInputName] = recDropzonePreloaded[
                recDropzoneFiles[thisImageKey].name
              ]
                ? recDropzonePreloaded[recDropzoneFiles[thisImageKey].name]
                : recDropzonePreloaded[thisImageInputKey];
            } else {
              formData[thisImageInputName] =
                thisImageKey in recDropzoneFilesCompressed
                  ? recDropzoneFilesCompressed[thisImageKey].dataURL
                  : recDropzoneFiles[thisImageKey].dataURL;
            }
          }
        });

      //apply any cleanup on payload data
      delete formData.action_type;

      formData.listing_price = parseFloat(formData.listing_price);
      formData.listing_price = formData.listing_price
        ? formData.listing_price
        : 0;

      //create attributes array
      formData.listing_attributes = {};
      formData.listing_attributes["seller_address_id"] =
        formData.seller_address_id;

      // handle dynamic list of attributes from proxy settings
      for (let la of recAppProxyConfig.settings.listing_attributes) {
        if (`listing_attribute_${la.attribute_name}` in formData) {
          formData.listing_attributes[la.attribute_name] =
            formData[`listing_attribute_${la.attribute_name}`];
          delete formData[`listing_attribute_${la.attribute_name}`];
        }
      }

      //remove fields for an unmatched listing
      if (formData.product_variant_id.length == 0) {
        delete formData.product_variant_id;
        delete formData.product_id;
      }

      /* Do a final check on the size of our payload and error/alert if it looks too big */
      if (getSizeInMB(formData) > 8) {
        btnUtils.reset();
        makeToast("toast-listing-request-size");
        return false;
      }

      try {
        let listingOrDraftPost = await fetch(postPath, {
          method: "POST",
          body: JSON.stringify(formData),
          headers: { "Content-Type": "application/json" },
        });
        const listingOrDraftResponse = await listingOrDraftPost.json();

        if (
          !listingOrDraftResponse.errors &&
          isObject(listingOrDraftResponse.data) &&
          "listing_status" in listingOrDraftResponse.data
        ) {
          let confirmUrl =
            recAppProxyConfig.paths.pathListingConfirmation.replace(
              "%lid%",
              listingOrDraftResponse.data.listing_id,
            );
          if (isDraft) {
            confirmUrl += "&rdraft";
          }
          if ("seller_prelogin" in formData) {
            confirmUrl += `&preloginSellerUUID=${formData.seller_id}&preloginListingId=${listingOrDraftResponse.data.listing_id}`;
          }
          if (thisCaller == "priceDrop") {
            document.location =
              recAppProxyConfig.paths.pathListings + `&t=tlpd`;
          } else {
            document.location = confirmUrl;
          }
        } else {
          btnUtils.reset();
          makeToast("toast-listing-error");
        }
      } catch (error) {
        btnUtils.reset();
        makeToast("toast-listing-error");
      }
    }
  };

  /* Listen for the submission of the seller listing form and do the thing */
  const sellerListingForms = document.querySelectorAll(".rec-slf-form");
  sellerListingForms.forEach((element) => {
    const sellerListingForm = element as HTMLFormElement;
    const sellerListingFormActionType = sellerListingForm.querySelector(
      ".rec-slf-form-action-type",
    ) as HTMLInputElement | null;
    const sellerListingFormActionDraft = sellerListingForm.querySelector(
      ".rec-slf-action-draft-create",
    ) as HTMLButtonElement | null;
    const sellerListingFormActionSaveDraft = sellerListingForm.querySelector(
      ".rec-slf-action-draft-save",
    ) as HTMLButtonElement | null;
    const sellerListingFormActionCreate = sellerListingForm.querySelector(
      ".rec-slf-action-create",
    ) as HTMLButtonElement | null;
    const sellerListingFormActionSave = sellerListingForm.querySelector(
      ".rec-slf-action-save",
    ) as HTMLButtonElement | null;
    const sellerAddressForm = sellerListingForm.querySelector(
      ".rec-shipping-address-form",
    ) as HTMLDivElement | null;
    const sellerAddressAvsContinue = sellerListingForm.querySelector(
      ".rec-slf-avs-complete",
    ) as HTMLButtonElement | null;

    if (sellerAddressAvsContinue) {
      sellerAddressAvsContinue.addEventListener("click", async (e) => {
        e.preventDefault();
        btnUtils.loadingToggle(e.target as HTMLButtonElement);
        await sellerListingFormExec(sellerListingForm, { caller: "avs" });
      });
    }

    /* Price Drop Functionality */
    const priceDropModal = document.getElementById(
      "rec-slf-price-drop",
    ) as HTMLDivElement | null;
    if (priceDropModal) {
      recModalOpen(priceDropModal.id);
      const priceDropCompleteButton = document.querySelector(
        ".rec-slf-price-drop-complete",
      ) as HTMLButtonElement | null;
      if (priceDropCompleteButton) {
        priceDropCompleteButton.addEventListener("click", async (e) => {
          e.preventDefault();
          btnUtils.loadingToggle(e.target as HTMLButtonElement);
          await sellerListingFormExec(sellerListingForm, {
            caller: "priceDrop",
          });
        });
      } else {
        console.error("Price drop complete button not found");
      }
    }

    /* Removal Functionality */
    const removalModal = document.getElementById("rec-slf-removal");
    if (removalModal) {
      recModalOpen(removalModal.id);
      const removalCompleteButton = document.querySelector(
        ".rec-slf-removal-complete",
      ) as HTMLButtonElement | null;
      if (removalCompleteButton) {
        removalCompleteButton.addEventListener("click", async (e) => {
          e.preventDefault();
          const target = e.currentTarget as HTMLButtonElement;
          btnUtils.loadingToggle(target);
          const listingId = target.getAttribute("data-listing");
          const listingStatus = target.getAttribute("data-listing-status");

          if (listingId && listingStatus) {
            const listingCancelPost = await fetch(
              recAppProxyConfig.paths.pathListingCancelPost.replace(
                "%lid%",
                listingId,
              ),
              {
                method: "POST",
                body: JSON.stringify({
                  listing_status: listingStatus,
                }),
                headers: { "Content-Type": "application/json" },
              },
            );
            const listingCancelPostResponse = await listingCancelPost.json();
            if (
              !listingCancelPostResponse.errors &&
              isObject(listingCancelPostResponse.data) &&
              "listing_status" in listingCancelPostResponse.data
            ) {
              document.location =
                recAppProxyConfig.paths.pathListings + `&t=tlcl`;
            } else {
              makeToast("toast-ajax-error");
              btnUtils.reset();
            }
          } else {
            console.error("Listing ID or status not found");
            btnUtils.reset();
          }
        });
      } else {
        console.error("Removal complete button not found");
      }
    }

    /* New Listing */
    if (sellerListingFormActionCreate) {
      sellerListingFormActionCreate.addEventListener("click", async (e) => {
        e.preventDefault();
        const callerValue = sellerListingForm.seller_prelogin ? "createDraft" : "create";
        if (sellerListingFormActionType) {
          sellerListingFormActionType.value = sellerListingForm.seller_prelogin ? "draft" : "listing";
        } else {
          console.error("Seller listing form action type input not found");
        }
        await addressFieldsApply(sellerListingForm);
        if (
          formValidation(sellerListingForm, "") &&
          sellingListingValidateImages(sellerListingForm, -1)
        ) {
          btnUtils.loadingToggle(e.target as HTMLButtonElement);
          if (recAppProxyConfig.settings.addresses?.avs && sellerAddressForm !== null) {
            const addressValidation = await addressValidator(sellerAddressForm);
            if (addressValidation.errors) {
              btnUtils.reset();
              makeToast("toast-ajax-error");
            } else if (
              addressValidation.data.addressesDiffer ||
              !addressValidation.data.avs.success
            ) {
              const avsBody = document.getElementById("rec-modal-avs-body");
              if (avsBody) {
                avsBody.innerHTML = addressValidation.avsModalContent;
                recModalOpen("rec-slf-avs", sellerListingFormActionCreate);
              } else {
                console.error("AVS modal body element not found");
              }
            } else {
              await sellerListingFormExec(sellerListingForm, {
                caller: callerValue,
              })
            }
          } else {
            await sellerListingFormExec(sellerListingForm, {
              caller: callerValue,
            });
          }
        }
      });
    } else {
      console.error("Seller listing form action create button not found");
    }

    /* Save Listing */
    if (sellerListingFormActionSave) {
      sellerListingFormActionSave.addEventListener("click", async (e) => {
        e.preventDefault();
        await addressFieldsApply(sellerListingForm);
        if (
          formValidation(sellerListingForm, "") &&
          sellingListingValidateImages(sellerListingForm, -1)
        ) {
          btnUtils.loadingToggle(e.target as HTMLButtonElement);
          if (recAppProxyConfig.settings.addresses?.avs) {
            if (sellerAddressForm !== null) {
              const addressValidation = await addressValidator(sellerAddressForm);
              if (addressValidation.errors) {
                btnUtils.reset();
                makeToast("toast-ajax-error");
              } else if (
                addressValidation.data.addressesDiffer ||
                !addressValidation.data.avs.success
              ) {
                const avsBody = document.getElementById("rec-modal-avs-body");
                if (avsBody) {
                  avsBody.innerHTML = addressValidation.avsModalContent;
                  recModalOpen("rec-slf-avs", sellerListingFormActionSave);
                } else {
                  console.error("AVS modal body element not found");
                }
              } else {
                await sellerListingFormExec(sellerListingForm, {
                  caller: "save",
                })
              }
            } else {
              await sellerListingFormExec(sellerListingForm, {
                caller: "save",
              });
            }
          } else {
            await sellerListingFormExec(sellerListingForm, { caller: "save" });
          }
        }
      });
    }

    /* New Draft */
    if (sellerListingFormActionDraft) {
      sellerListingFormActionDraft.addEventListener("click", async (e) => {
        e.preventDefault();
        if (sellerListingFormActionType) {
          sellerListingFormActionType.value = "draft";
        } else {
          console.error("Seller listing form action type input not found");
        }
        addressFieldsApply(sellerListingForm);
        if (formValidation(sellerListingForm, "rec-required-draft-ignore")) {
          btnUtils.loadingToggle(e.target as HTMLButtonElement);
          await sellerListingFormExec(sellerListingForm, {
            caller: "createDraft",
          });
        }
      });
    }

    /* Save Draft */
    if (sellerListingFormActionSaveDraft) {
      sellerListingFormActionSaveDraft.addEventListener("click", async (e) => {
        e.preventDefault();
        await addressFieldsApply(sellerListingForm);
        if (formValidation(sellerListingForm, "rec-required-draft-ignore")) {
          btnUtils.loadingToggle(e.target as HTMLButtonElement);
          await sellerListingFormExec(sellerListingForm, {
            caller: "saveDraft",
          });
        }
      });
    }

    imageDropzonesInit(
      `#${sellerListingForm.id}`,
      true,
      dropzoneObjects,
      recDropzoneFiles,
      recDropzoneFilesCompressed,
      recDropzonePreloaded,
    );
    const isMobile = isMobileCheck();
    /* Setup the Drag n Drop capability of the seller listing form's image dropzones */
    if (!isMobile) {
      const sortableElement = sellerListingForm.querySelector(
        ".rec-images-sortable",
      ) as HTMLDivElement | null;
      if (sortableElement) {
        imageDropzonesDnD(sortableElement);
      } else {
        console.error("Sortable element not found");
      }
    }
  });

  /* Initialize an interval to check for QR listing images every X seconds */
  if (recAppProxyConfig.settings.slf.qr_images) {
    const checkQRImages = async () => {
      try {
        const sessionId = document.querySelector(
          "#session-id",
        ) as HTMLInputElement | null;
        let sessionIdReplace = "";
        if (sessionId === null) {
          console.error("Session ID input element not found");
          sessionIdReplace = "no-session-id";
        } else {
          sessionIdReplace = sessionId.value;
        }
        const qrImageCheckPath =
          recAppProxyConfig.paths.pathQRListingImagesCheck.replace(
            "%sid%",
            sessionIdReplace,
          );

        const qrImagesCheck = await fetch(qrImageCheckPath, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        const qrImagesPostResponse = await qrImagesCheck.json();

        if (!qrImagesPostResponse.errors && qrImagesPostResponse.hasImages) {
          for (let i = 1; i <= 5; i++) {
            const preloadedImageKey = "listing_image" + i;
            if (preloadedImageKey in qrImagesPostResponse) {
              recDropzonePreloaded[preloadedImageKey] =
                qrImagesPostResponse[preloadedImageKey];
              dropzoneObjects[i - 1].displayExistingFile(
                { name: preloadedImageKey, size: 12345 },
                qrImagesPostResponse[preloadedImageKey],
                undefined,
                undefined,
                false,
              );
            }
          }
          /**
           * Temporary solution?
           * Hide qr code and clear checkQRImages interval to prevent unwanted behavior
           */
          const qrImagesQR = document.querySelector(
            "#rec-qr-image-container",
          ) as HTMLDivElement | null;
          if (qrImagesQR) {
            qrImagesQR.style.visibility = "hidden";
          }
          clearInterval(intervalId);
        }
      } catch (error) {
        clearInterval(intervalId);
      }
    };

    var intervalId = window.setInterval(checkQRImages, 5000);
    window.setTimeout(function() {
      clearInterval(intervalId);
    }, 300000);
  }
};

async function addressValidator(addressInputContainer: HTMLDivElement) {
  const addressFormData = serializeArray(addressInputContainer);
  //normalize field property names of the address
  const addressVerifyInput = {
    first_name: addressFormData.seller_first_name,
    last_name: addressFormData.seller_last_name,
    street1: addressFormData.seller_address_line1,
    street2: addressFormData.seller_address_line2,
    city: addressFormData.seller_city,
    state: addressFormData.seller_state,
    zip: addressFormData.seller_postal,
    country: addressFormData.seller_country,
    phone: addressFormData.seller_phone,
    company: "",
  };

  let addressVerifyPost = await fetch(
    recAppProxyConfig.paths.pathAddressVerify,
    {
      method: "POST",
      body: JSON.stringify(addressVerifyInput),
      headers: { "Content-Type": "application/json" },
    },
  );
  const addressVerifyResponse = await addressVerifyPost.json();

  return addressVerifyResponse;
}

/* Drag n Drop */
function imageDropzonesDnD(elem: HTMLDivElement) {
  // Pull the texts of the various dropzones so we preserve that order
  let dropzoneHTMLs: string[] = [],
    dropzoneTexts: string[] = [],
    dropzoneLabels: (string | null)[] = [];
  elem.querySelectorAll(".rec-image-dropzone-text").forEach((thisDropText) => {
    dropzoneTexts.push(thisDropText.textContent?.trim() || "");
    dropzoneHTMLs.push(thisDropText.innerHTML.trim());
    dropzoneLabels.push(
      thisDropText.closest(".rec-image-dropzone")?.getAttribute("aria-label") ||
      null,
    );
  });

  new Sortable(elem, {
    touchStartThreshold: 4,
    animation: 150,
    ghostClass: "rec-sortable-ghost",
    chosenClass: "rec-sortable-chosen",
    dragClass: "rec-sortable-drag",
    onChange: function(evt) {
      elem
        .querySelectorAll(".rec-image-dropzone-text")
        .forEach((thisDropText, index) => {
          thisDropText.innerHTML = dropzoneHTMLs[index];
          const dzParent = thisDropText.closest(".rec-image-dropzone");
          if (dzParent) {
            dzParent.setAttribute("data-rec-text", dropzoneTexts[index]);
            dzParent.setAttribute("aria-label", dropzoneLabels[index] || "");
            const removeLink = dzParent.querySelector("a.dz-remove");
            if (removeLink) {
              removeLink.setAttribute(
                "aria-label",
                `Remove ${dropzoneTexts[index].toLowerCase()} image`,
              );
            }
          }
        });
    },
  });
}
